import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * Shows the exact date windows the KPI cards are comparing — so the CEO can
 * see "Jan 1 – May 29, 2026 vs Jan 1 – May 29, 2025" instead of guessing
 * what "prior period" means.
 *
 * `meta` comes verbatim from the /metrics/kpis response.
 */

function fmt(iso) {
  // iso is already a date string like "2025-05-29" — render in viewer locale
  // without timezone arithmetic.
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function rangeLabel(start, end) {
  if (start === end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

export default function ComparisonBanner({ meta, loading }) {
  if (loading || !meta) return null;
  const modeLabel =
    meta.compare_mode === "yoy" ? "Same period last year" : "Prior period";
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground border border-dashed border-border/70 rounded-md px-3 py-2"
      data-testid="comparison-banner"
    >
      <span className="uppercase tracking-[0.14em] font-medium text-foreground/70">
        Comparing
      </span>
      <span
        className="font-mono text-foreground"
        data-testid="comparison-current-range"
      >
        {rangeLabel(meta.current_start, meta.current_end)}
      </span>
      <ArrowRight className="h-3.5 w-3.5 opacity-50" />
      <span
        className="font-mono text-foreground"
        data-testid="comparison-prev-range"
      >
        {rangeLabel(meta.prev_start, meta.prev_end)}
      </span>
      <span
        className="text-muted-foreground italic"
        data-testid="comparison-mode-label"
      >
        ({modeLabel})
      </span>
    </div>
  );
}
